import React, { ChangeEvent, useEffect, useMemo, useState } from "react";

import { Box, Button, Container, IconButton, Typography } from "@material-ui/core";

import { useTranslation } from "react-i18next";

import { useNavigate } from "react-router";

import CartItem from "components/Cart/CartItem";
import PageTitle from "components/PageTitle";
import DesktopContainerDrawer from "containers/DesktopContainerDrawer";
import MobileContainerDrawer from "containers/MobileContainerDrawer";
import makeStyles from "hooks/makeStyles";
import useBusiness from "hooks/useBusiness";
import useCart from "hooks/useCart";
import CartIcon from "icons/CartIcon";
import CloseCircle from "icons/CloseCircle";
import TrashIcon from "icons/TrashIcon";
import { formatMoney } from "utility/formatMoney";

const useStyles = makeStyles(theme => ({
	root: {
		padding: 0,
		display: "flex",
		flex: 1,
		flexDirection: "column",
		height: "calc(var(--vh, 1vh) * 100 - 95px)", // not great..
		position: "relative",
		[theme.breakpoints.up(480)]: {
			overflow: "hidden",
			height: "100vh"
		}
	},
	body: {
		[theme.breakpoints.up(480)]: {
			backgroundColor: theme.palette.background.default,
			color: theme.palette.text.primary,
			borderBottomLeftRadius: theme.spacing(2),
			borderBottomRightRadius: theme.spacing(2),
			padding: theme.spacing(4)
		}
	},
	title: {
		alignSelf: "flex-start",
		textAlign: "left",
		textTransform: "uppercase",
		fontWeight: 500,
		fontSize: 16,
		letterSpacing: "0.38px"
	},
	items: {
		marginBottom: theme.spacing(2)
	},
	item: {
		marginLeft: theme.spacing(2),
		marginTop: theme.spacing(2),
		marginRight: theme.spacing(2),
		display: "flex",
		flexDirection: "row",
		alignItems: "center",
		justifyContent: "space-between"
	},
	itemTitle: {
		textTransform: "uppercase",
		fontSize: 14,
		letterSpacing: "0.38px"
	},
	radioIcon: {
		borderRadius: "50%",
		width: 13,
		height: 13,
		border: "1px solid #f2f2f2"
	},
	titleRow: {
		marginTop: theme.spacing(1),
		marginBottom: theme.spacing(1),
		display: "flex",
		flexDirection: "row",
		alignItems: "center"
	},
	icon: {
		height: theme.spacing(2),
		marginRight: theme.spacing(1),
		fill: "#fff",
		[theme.breakpoints.up(480)]: {
			fill: theme.palette.text.primary
		}
	},
	trashIcon: {
		height: 18,
		marginRight: theme.spacing(1),
		fill: "#fff",
		[theme.breakpoints.up(480)]: {
			fill: theme.palette.text.primary
		}
	},
	scroll: {
		height: "calc(var(--vh, 1vh) * 100 - 195px)",
		position: "relative",
		paddingRight: 20, // improves scrollbar placement
		marginRight: -12, // improves scrollbar placement
		overflowX: "initial",
		overflowY: "scroll",
		paddingBottom: 40,
		[theme.breakpoints.up(480)]: {
			display: "flex",
			flexDirection: "column",
			overflowY: "auto",
			paddingRight: 12,
			marginRight: -12
		}
	},
	scrollBox: {
		display: "flex",
		flexDirection: "column"
	},
	footer: {
		backgroundColor: "transparent",
		bottom: 0,
		left: 0,
		right: 0,
		width: "100%",
		marginTop: theme.spacing(1),
		display: "flex",
		position: "absolute",
		[theme.breakpoints.up(480)]: {
			bottom: "6px",
			marginTop: 0,
			width: "auto",
			flex: "1",
			justifyContent: "center",
			padding: theme.spacing(2)
		}
	},
	subTotalRow: {
		marginTop: theme.spacing(1),
		padding: theme.spacing(1),
		display: "flex",
		flexDirection: "row",
		justifyContent: "space-between"
	},
	availabilityWarningRow: {
		margin: `0 ${theme.spacing(1)}px 0 ${theme.spacing(1)}px`
	},
	availabilityWarningRowDesktop: {
		margin: 0
	},
	availabilityWarning: {
		fontSize: "14px",
		color: theme.palette.warning.main
	},
	subTotalLabel: {
		alignContent: "left",
		textTransform: "uppercase",
		color: "#fff",
		[theme.breakpoints.up(480)]: {
			color: theme.palette.text.primary
		}
	},
	subTotal: {
		fontSize: 16,
		alignContent: "right"
	},
	clearButton: {
		[theme.breakpoints.up(480)]: {
			minWidth: "auto",
			marginLeft: theme.spacing(1),
			verticalAlign: "top",
			marginTop: "4px"
		},
		padding: 0
	},
	addButton: {
		backgroundColor: theme.palette.primary.main,
		color: theme.palette.primary.contrastText,
		borderRadius: 24,
		fontSize: 16,
		height: 48,
		textTransform: "uppercase",
		flex: "1",
		"&:hover": {
			[theme.breakpoints.up(480)]: {
				backgroundColor: theme.palette.primary.main,
				color: theme.palette.primary.contrastText
			}
		}
	},
	actionButtonLabel: {
		marginLeft: theme.spacing(0.5)
	}
}));

interface Props {
	onChange: (orderTypeId: string) => void;
}

const CartModal = ({ onChange }: Props) => {
	const classes = useStyles();
	const { t } = useTranslation();
	const navigate = useNavigate();
	const { business } = useBusiness();
	const { cartTotalCents, cartItems, clearCart, areAllItemsAvailable } = useCart();
	const [isCheckoutEnabled, setIsCheckoutEnabled] = useState(true);

	useEffect(() => {
		if (cartItems.length === 0 && isCheckoutEnabled) {
			setIsCheckoutEnabled(false);
		}
	}, [cartItems, isCheckoutEnabled, setIsCheckoutEnabled]);

	const allItemsAreAvailable = areAllItemsAvailable();

	const handleChange = useMemo(() => (event: ChangeEvent<HTMLInputElement>, value: string) => onChange(value), [
		onChange
	]);

	const onCheckoutClicked = () => {
		navigate("#checkout");
	};

	const clearOrderClicked = () => {
		clearCart();
	};

	return (
		<Container className={classes.root}>
			<PageTitle businessName={business.name} subTitle={t("cart")} />
			<Box className={classes.body}>
				<Box className={classes.titleRow}>
					<MobileContainerDrawer>
						<CartIcon height={22} width={22} className={classes.icon} />
					</MobileContainerDrawer>
					<Typography className={classes.title}>{t("cart")}</Typography>
				</Box>
				<Box className={classes.scroll}>
					<Box className={classes.scrollBox}>
						{cartItems && cartItems.map((c, indx) => <CartItem cartItem={c} key={indx} />)}
						<MobileContainerDrawer>
							{allItemsAreAvailable ? null : (
								<Box className={classes.availabilityWarningRow}>
									<Typography className={classes.availabilityWarning}>
										Some items in your cart are unavailable at the selected time
									</Typography>
								</Box>
							)}
							<Box className={classes.subTotalRow}>
								<Button onClick={clearOrderClicked} className={classes.clearButton} disabled={cartItems.length === 0}>
									<TrashIcon className={classes.trashIcon} />
									<Typography className={classes.subTotalLabel}>{t("clearOrder")}</Typography>
								</Button>
								<Typography className={classes.subTotalLabel}>{t("subTotal")}</Typography>
							</Box>
							<Box className={classes.subTotalRow}>
								<Typography className={classes.itemTitle}></Typography>
								<Typography className={classes.subTotal}>{formatMoney(cartTotalCents)}</Typography>
							</Box>
						</MobileContainerDrawer>
						<DesktopContainerDrawer>
							{allItemsAreAvailable ? null : (
								<Box className={classes.availabilityWarningRowDesktop}>
									<Typography className={classes.availabilityWarning}>
										Some items in your cart are unavailable at the selected time
									</Typography>
								</Box>
							)}
							<Box className={classes.subTotalRow}>
								<Typography className={classes.subTotalLabel}>
									{t("subTotal")}
									<IconButton
										className={classes.clearButton}
										onClick={clearOrderClicked}
										title={t("clearOrder")}
										disabled={cartItems.length === 0}
									>
										<MobileContainerDrawer>
											<CloseCircle className={classes.icon} />
										</MobileContainerDrawer>
										<DesktopContainerDrawer>
											<CloseCircle className={classes.icon} />
										</DesktopContainerDrawer>
									</IconButton>
								</Typography>
								<Typography className={classes.subTotal}>{formatMoney(cartTotalCents)}</Typography>
							</Box>
						</DesktopContainerDrawer>
					</Box>
				</Box>
			</Box>
			<Box className={classes.footer}>
				<Button
					variant="outlined"
					className={classes.addButton}
					onClick={onCheckoutClicked}
					disabled={!isCheckoutEnabled || !allItemsAreAvailable}
				>
					{formatMoney(cartTotalCents)}
					<Typography className={classes.actionButtonLabel}>{` - ${t("checkout")}`}</Typography>
				</Button>
			</Box>
		</Container>
	);
};

export default CartModal;
